import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { LinearProgress, Typography, Tooltip, Paper } from '@material-ui/core';
import InfoIcon from '@mui/icons-material/InfoOutlined';

import { selectUser } from 'entities/user/selectors';
import { useGivingAccountsSummaryQuery } from 'generated/graphql';
import BackButtonLayoutTitleAction from 'retired/BackButtonLayout/BackButtonLayoutTitleAction';
import { currencyFormat } from 'utils/currencyFormat';
import { gray, white } from 'utils/color-styles';
import accountsSVG from 'assets/budgetLedger/accounts.avif';
import { Privileges } from 'utils/privileges';

import GivingAccountCard from './GivingAccountCard';
import { CreateGivingAccountModal } from './CreateGivingAccountModal';

const GivingAccountPage = (): JSX.Element => {
  const history = useHistory();

  const user = useSelector(selectUser);

  const budgetLedgerEnabled = user && user.hasPrivilege(Privileges.BUDGET_LEDGER_VIEW);
  if (user && !budgetLedgerEnabled) {
    history.replace('/');
  }

  const { data, loading, error } = useGivingAccountsSummaryQuery({});

  // If only one account, go directly to its page
  const hasOneAccount = data?.givingAccounts.length === 1;
  useEffect(() => {
    if (hasOneAccount) {
      history.replace(`/donations/ledger/${data?.givingAccounts[0]?.id}`);
    }
  }, [hasOneAccount]);
  if (hasOneAccount || loading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <>
        <Helmet title="Giving Accounts" />
        <div>There was an error loading this page. Please try again.</div>
      </>
    );
  }

  const accumulativeBalance = data?.givingAccounts
    .map((acc) => acc?.balanceUserCurrency || acc?.balance || 0)
    .reduce((a, b) => a + b, 0);

  return (
    <>
      <Helmet title="Giving Accounts" />
      <BackButtonLayoutTitleAction to="/donations" title="Giving Accounts">
        <div>
          <Paper
            style={{
              padding: '24px 36px',
              marginBottom: 40,
              backgroundColor: white,
              backgroundImage: `url("${accountsSVG}")`,
              backgroundSize: 'contain',
              backgroundPositionX: '90%',
              minHeight: 200,
            }}
          >
            <Typography style={{ color: gray }}>
              Estimated Total Balance{' '}
              <Tooltip title="This balance is an estimated sum of all historical transactions across all of your Account Balances. If Account Balances were converted from another currency, this amount is an estimate based on the current exchange rate, which will vary.">
                <InfoIcon style={{ verticalAlign: '-20%' }} />
              </Tooltip>
            </Typography>
            <Typography style={{ fontSize: 42, fontWeight: 'bold', marginBottom: 40 }}>
              {user && accumulativeBalance
                ? currencyFormat(accumulativeBalance, user.displayCurrency)
                : '-'}
            </Typography>
          </Paper>

          {user?.isAdmin && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
              <CreateGivingAccountModal title="Create a new Giving Account" />
            </div>
          )}

          <div
            style={{
              // 3-column grid
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              columnGap: 20,
              rowGap: 20,
            }}
          >
            {data?.givingAccounts.length === 0 ? <div>No Accounts</div> : null}
            {data?.givingAccounts.map((account) => (
              <Link
                key={account?.id}
                to={`/donations/ledger/${account?.id}`}
                style={{
                  // Stretch children to be of equal height
                  display: 'flex',
                }}
              >
                <GivingAccountCard givingAccount={account} />
              </Link>
            ))}
          </div>
        </div>
      </BackButtonLayoutTitleAction>
    </>
  );
};

export default GivingAccountPage;
